import type { ReactNode } from "react";

/**
 * The cogent's messages as markdown: paragraphs, `-` and `1.` lists, **bold**,
 * *emphasis*, `code` and [links](https://…). Built as React elements, never
 * as HTML, so a message cannot inject markup.
 */
export function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  const flushPara = () => {
    if (para.length)
      blocks.push(<p key={blocks.length}>{inline(para.join(" "))}</p>);

    para = [];
  };
  const flushList = () => {
    if (!list) return;

    const items = list.items.map((item, i) => <li key={i}>{inline(item)}</li>);
    blocks.push(
      list.ordered ? (
        <ol key={blocks.length}>{items}</ol>
      ) : (
        <ul key={blocks.length}>{items}</ul>
      ),
    );
    list = null;
  };

  for (const raw of text.split("\n")) {
    const line = raw.trim();
    const item = line.match(/^(?:[-*•]|\d+[.)])\s+/);

    if (!line) {
      flushPara();
      flushList();
    } else if (item) {
      flushPara();
      const ordered = /^\d/.test(item[0]);

      if (list && list.ordered !== ordered) flushList();

      list ??= { ordered, items: [] };
      list.items.push(line.slice(item[0].length));
    } else if (list && raw.startsWith("  ")) {
      list.items[list.items.length - 1] += ` ${line}`;
    } else {
      flushList();
      para.push(line.replace(/^#+\s*/, ""));
    }
  }

  flushPara();
  flushList();
  return <>{blocks}</>;
}

const inlinePattern =
  /\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_|`(.+?)`|\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g;

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let at = 0;

  for (const m of text.matchAll(inlinePattern)) {
    if (m.index > at) out.push(text.slice(at, m.index));

    const k = out.length;
    const emphasis = m[2] ?? m[3];

    if (m[1]) out.push(<strong key={k}>{inline(m[1])}</strong>);
    else if (emphasis) out.push(<em key={k}>{inline(emphasis)}</em>);
    else if (m[4]) out.push(<code key={k}>{m[4]}</code>);
    else
      out.push(
        <a key={k} href={m[6]} target="_blank" rel="noreferrer">
          {m[5]}
        </a>,
      );

    at = m.index + m[0].length;
  }

  if (at < text.length) out.push(text.slice(at));

  return out;
}
